import { useQuery } from "@apollo/client";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Link,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Title, useTranslate } from "react-admin";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { RequirePermission } from "../components/RequirePermission";
import { ResourceName, SALESFORCE_DASHBOARD_ROUTE } from "../constants/resources";
import {
  CrmObject,
  CustomerServiceSalesforceContractsDocument,
  CustomerServiceSalesforceContractsQuery,
  CustomerServiceSalesforceRecordsDocument,
  CustomerServiceSalesforceRecordsQuery,
  SalesforceRecordsKind,
} from "../generated/graphql";
import { crmRecordPath } from "../lib/crm";
import {
  BLANK_CATEGORY_LABEL,
  cancellationTypeLabelKey,
  formatChartValue,
  formatCount,
  parseDrillDown,
  SALESFORCE_CHART_COLORS,
  type SalesforceDrillDown,
} from "../lib/salesforceDashboard";
import { color, space } from "../theme/designTokens";

const colors = SALESFORCE_CHART_COLORS;
const ICON_TILE_SIZE_PX = 32;

const dateFormat = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeZone: "Europe/Berlin" });
const dateTimeFormat = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Europe/Berlin",
});
const priceFormat = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
// Salesforce dates arrive as plain yyyy-mm-dd; parse them as UTC so the Berlin zone cannot shift the day.
const formatDate = (value: string | null | undefined): string =>
  value ? dateFormat.format(new Date(`${value}T00:00:00Z`)) : BLANK_CATEGORY_LABEL;

const cellOrBlank = (value: string | null | undefined): string => value || BLANK_CATEGORY_LABEL;

type Column<Row> = {
  key: string;
  header: string;
  render: (row: Row) => ReactNode;
  // Value the column sorts by; strings compare in German collation, numbers numerically.
  sortValue: (row: Row) => string | number | null;
  align?: "right";
};

type SortState = { key: string; direction: "asc" | "desc" };

const germanCollator = new Intl.Collator("de", { numeric: true });

const compareValues = (a: string | number | null, b: string | number | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return typeof a === "number" && typeof b === "number" ? a - b : germanCollator.compare(String(a), String(b));
};

const DataTable = <Row extends { salesforceId: string }>({
  columns,
  rows,
}: {
  columns: Column<Row>[];
  rows: Row[];
}) => {
  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [horizontalScroll, setHorizontalScroll] = useState({ max: 0, value: 0 });
  const [sort, setSort] = useState<SortState | null>(null);
  useLayoutEffect(() => {
    const container = containerRef.current;
    const table = tableRef.current;
    if (!container || !table) return;

    const updateScroll = () => {
      const max = Math.max(0, container.scrollWidth - container.clientWidth);
      const value = Math.min(max, Math.max(0, container.scrollLeft));
      if (container.scrollLeft !== value) container.scrollLeft = value;
      setHorizontalScroll(current => (current.max === max && current.value === value ? current : { max, value }));
    };
    updateScroll();
    container.addEventListener("scroll", updateScroll);
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updateScroll);
    observer?.observe(container);
    observer?.observe(table);
    return () => {
      container.removeEventListener("scroll", updateScroll);
      observer?.disconnect();
    };
  }, []);
  const sortedRows = useMemo(() => {
    const column = columns.find(candidate => candidate.key === sort?.key);
    if (!sort || !column) return rows;
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => factor * compareValues(column.sortValue(a), column.sortValue(b)));
  }, [columns, rows, sort]);

  const toggleSort = (key: string) =>
    setSort(current =>
      current?.key === key && current.direction === "asc" ? { key, direction: "desc" } : { key, direction: "asc" }
    );

  return (
    <>
      {horizontalScroll.max > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: `${space.lg}px`, px: `${space.lg}px` }}>
          <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap", color: colors.text }}>
            {translate("salesforceDashboard.records.horizontalScroll")}
          </Typography>
          <Slider
            aria-label={translate("salesforceDashboard.records.horizontalScroll")}
            max={horizontalScroll.max}
            onChange={(_event, value) => {
              if (typeof value !== "number" || !containerRef.current) return;
              containerRef.current.scrollLeft = value;
              setHorizontalScroll(current => ({ ...current, value }));
            }}
            size="small"
            value={horizontalScroll.value}
          />
        </Box>
      )}
      <TableContainer
        ref={containerRef}
        sx={{ maxHeight: "calc(100vh - 260px)", backgroundColor: colors.cardBackground }}
      >
        <Table ref={tableRef} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  align={column.align}
                  key={column.key}
                  sortDirection={sort?.key === column.key ? sort.direction : false}
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={sort?.key === column.key}
                    direction={sort?.key === column.key ? sort.direction : "asc"}
                    onClick={() => toggleSort(column.key)}
                  >
                    {column.header}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map(row => (
              <TableRow hover key={row.salesforceId}>
                {columns.map(column => (
                  <TableCell align={column.align} key={column.key} sx={{ whiteSpace: "nowrap" }}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

type SubscriptionRow = CustomerServiceSalesforceRecordsQuery["customerServiceSalesforceRecords"]["records"][number];
type ContractRow = CustomerServiceSalesforceContractsQuery["customerServiceSalesforceContracts"]["records"][number];

const SubscriptionTable = ({ rows }: { rows: SubscriptionRow[] }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`salesforceDashboard.records.columns.${key}`);
  const cancellationLabel = (value: string | null | undefined): string => {
    const key = value ? cancellationTypeLabelKey(value) : undefined;
    return key ? translate(`salesforceDashboard.cancellationTypes.${key}`) : cellOrBlank(value);
  };
  const text = (key: string, value: (row: SubscriptionRow) => string | null | undefined): Column<SubscriptionRow> => ({
    key,
    header: t(key),
    render: row => cellOrBlank(value(row)),
    sortValue: row => value(row) || null,
  });
  const date = (key: string, value: (row: SubscriptionRow) => string | null | undefined): Column<SubscriptionRow> => ({
    key,
    header: t(key),
    render: row => formatDate(value(row)),
    sortValue: row => value(row) || null,
  });
  const columns: Column<SubscriptionRow>[] = [
    text("id", row => row.salesforceId),
    text("product", row => row.productName),
    text("family", row => row.productFamily),
    text("status", row => row.status),
    text("segment", row => row.segment),
    {
      key: "createdAt",
      header: t("createdAt"),
      render: row => (row.createdAt ? dateTimeFormat.format(new Date(row.createdAt)) : BLANK_CATEGORY_LABEL),
      sortValue: row => row.createdAt ?? null,
    },
    date("startDate", row => row.startDate),
    date("cancellationDate", row => row.cancellationDate),
    date("endDate", row => row.endDate),
    {
      key: "cancellationType",
      header: t("cancellationType"),
      render: row => cancellationLabel(row.cancellationType),
      sortValue: row => row.cancellationType ?? null,
    },
    text("sponsor", row => row.sponsoringKrankenkasse),
    text("contract", row => row.contractSalesforceId),
    text("contractStatus", row => row.contractStatus),
  ];
  return <DataTable columns={columns} rows={rows} />;
};

const LicenceTable = ({ rows }: { rows: SubscriptionRow[] }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`salesforceDashboard.records.licenceColumns.${key}`);
  const text = (key: string, value: (row: SubscriptionRow) => string | null | undefined): Column<SubscriptionRow> => ({
    key,
    header: t(key),
    render: row => cellOrBlank(value(row)),
    sortValue: row => value(row) || null,
  });
  const date = (key: string, value: (row: SubscriptionRow) => string | null | undefined): Column<SubscriptionRow> => ({
    key,
    header: t(key),
    render: row => formatDate(value(row)),
    sortValue: row => value(row) || null,
  });
  const linked = (
    key: string,
    object: CrmObject,
    label: (row: SubscriptionRow) => string | null | undefined,
    id: (row: SubscriptionRow) => string | null | undefined
  ): Column<SubscriptionRow> => ({
    key,
    header: t(key),
    render: row => {
      const recordId = id(row);
      const value = cellOrBlank(label(row) || recordId);
      return recordId ? (
        <Link component={RouterLink} sx={{ color: colors.link }} to={crmRecordPath(object, recordId)}>
          {value}
        </Link>
      ) : (
        value
      );
    },
    sortValue: row => label(row) || id(row) || null,
  });
  const flag = (key: string, value: (row: SubscriptionRow) => boolean | null | undefined): Column<SubscriptionRow> => ({
    key,
    header: t(key),
    render: row =>
      value(row) == null ? (
        BLANK_CATEGORY_LABEL
      ) : (
        <Checkbox checked={value(row) ?? false} disabled inputProps={{ "aria-label": t(key) }} size="small" />
      ),
    sortValue: row => (value(row) == null ? null : Number(value(row))),
  });
  const columns: Column<SubscriptionRow>[] = [
    text("product", row => row.productName),
    {
      key: "createdAt",
      header: t("createdAt"),
      render: row => (row.createdAt ? dateFormat.format(new Date(row.createdAt)) : BLANK_CATEGORY_LABEL),
      sortValue: row => row.createdAt ?? null,
    },
    text("segment", row => row.segment),
    linked(
      "contractNumber",
      CrmObject.Contract,
      row => row.contractNumber,
      row => row.contractSalesforceId
    ),
    text("contractRecordType", row => row.contractRecordType),
    text("contractName", row => row.contractName),
    linked(
      "name",
      CrmObject.ContractItem,
      row => row.name,
      row => row.salesforceId
    ),
    text("recordType", row => row.recordType),
    date("startDate", row => row.startDate),
    {
      key: "trialPeriodDays",
      header: t("trialPeriodDays"),
      align: "right",
      render: row => (row.trialPeriodDays == null ? BLANK_CATEGORY_LABEL : formatCount(row.trialPeriodDays)),
      sortValue: row => row.trialPeriodDays ?? null,
    },
    date("billingStartDate", row => row.billingStartDate),
    date("endDate", row => row.endDate),
    {
      key: "price",
      header: t("price"),
      align: "right",
      render: row => (row.price == null ? BLANK_CATEGORY_LABEL : priceFormat.format(Number(row.price))),
      sortValue: row => (row.price == null ? null : Number(row.price)),
    },
    text("status", row => row.status),
    text("assetType", row => row.assetType),
    linked(
      "changedFrom",
      CrmObject.ContractItem,
      row => row.changedFromName,
      row => row.changedFromSalesforceId
    ),
    linked(
      "changedInto",
      CrmObject.ContractItem,
      row => row.changedIntoName,
      row => row.changedIntoSalesforceId
    ),
    flag("inTrial", row => row.inTrial),
    {
      key: "cancellationType",
      header: t("cancellationType"),
      render: row => {
        const key = row.cancellationType ? cancellationTypeLabelKey(row.cancellationType) : undefined;
        return key ? translate(`salesforceDashboard.cancellationTypes.${key}`) : cellOrBlank(row.cancellationType);
      },
      sortValue: row => row.cancellationType ?? null,
    },
    text("lostReason", row => row.lostReason),
    flag("billingStarted", row => row.billingStarted),
    text("productId", row => row.productSalesforceId),
    text("contractId", row => row.contractSalesforceId),
    text("id", row => row.salesforceId),
  ];
  return <DataTable columns={columns} rows={rows} />;
};

const ContractTable = ({ rows }: { rows: ContractRow[] }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`salesforceDashboard.records.columns.${key}`);
  const numeric = (value: string | null | undefined): number | null => (value == null ? null : Number(value));
  const columns: Column<ContractRow>[] = [
    { key: "id", header: t("id"), render: row => row.salesforceId, sortValue: row => row.salesforceId },
    {
      key: "status",
      header: t("status"),
      render: row => cellOrBlank(row.status),
      sortValue: row => row.status ?? null,
    },
    {
      key: "segment",
      header: t("segment"),
      render: row => cellOrBlank(row.recordType),
      sortValue: row => row.recordType ?? null,
    },
    {
      key: "startDate",
      header: t("startDate"),
      render: row => formatDate(row.startDate),
      sortValue: row => row.startDate ?? null,
    },
    {
      key: "endDate",
      header: t("endDate"),
      render: row => formatDate(row.endDate),
      sortValue: row => row.endDate ?? null,
    },
    {
      key: "mrr",
      header: t("mrr"),
      align: "right",
      render: row => (row.mrr == null ? BLANK_CATEGORY_LABEL : formatChartValue(Number(row.mrr))),
      sortValue: row => numeric(row.mrr),
    },
    {
      key: "activeItems",
      header: t("activeItems"),
      align: "right",
      render: row =>
        row.activeSubscriptionItems == null ? BLANK_CATEGORY_LABEL : formatCount(Number(row.activeSubscriptionItems)),
      sortValue: row => numeric(row.activeSubscriptionItems),
    },
  ];
  return <DataTable columns={columns} rows={rows} />;
};

type RecordListState = { loading: boolean; failed: boolean; totalCount: number; table: ReactNode };

const useRecordList = (drillDown: SalesforceDrillDown | null): RecordListState => {
  const recordsQuery = useQuery<CustomerServiceSalesforceRecordsQuery>(CustomerServiceSalesforceRecordsDocument, {
    variables: { input: drillDown?.kind === "records" ? drillDown.input : undefined },
    skip: drillDown?.kind !== "records",
    fetchPolicy: "cache-and-network",
  });
  const contractsQuery = useQuery<CustomerServiceSalesforceContractsQuery>(CustomerServiceSalesforceContractsDocument, {
    variables: { segment: drillDown?.kind === "contracts" ? drillDown.segment : undefined },
    skip: drillDown?.kind !== "contracts",
    fetchPolicy: "cache-and-network",
  });

  if (drillDown?.kind === "contracts") {
    const list = contractsQuery.data?.customerServiceSalesforceContracts;
    return {
      loading: contractsQuery.loading && !list,
      failed: Boolean(contractsQuery.error),
      totalCount: list?.totalCount ?? 0,
      table: list && <ContractTable rows={list.records} />,
    };
  }
  const list = recordsQuery.data?.customerServiceSalesforceRecords;
  return {
    loading: recordsQuery.loading && !list,
    failed: Boolean(recordsQuery.error),
    totalCount: list?.totalCount ?? 0,
    table:
      list &&
      (drillDown?.kind === "records" && drillDown.input.kind === SalesforceRecordsKind.ActiveLicences ? (
        <LicenceTable rows={list.records} />
      ) : (
        <SubscriptionTable rows={list.records} />
      )),
  };
};

// Mirrors SALESFORCE_DRILL_DOWN_LIMIT in the backend.
const SHOWN_RECORD_LIMIT = 500;

const RecordsContent = ({ drillDown }: { drillDown: SalesforceDrillDown }) => {
  const translate = useTranslate();
  const { loading, failed, totalCount, table } = useRecordList(drillDown);

  return (
    <Box sx={{ p: `${space.xl}px` }}>
      <Title title={drillDown.title} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: `${space.xl}px`,
          mb: `${space.lg}px`,
          p: `${space.lg}px ${space.xl}px`,
          backgroundColor: colors.headerBackground,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: "4px",
          color: colors.text,
        }}
      >
        <Box sx={{ display: "flex", gap: `${space.lg}px`, minWidth: 0 }}>
          <Box
            aria-hidden
            sx={{
              display: "flex",
              flex: "none",
              alignItems: "center",
              justifyContent: "center",
              width: `${ICON_TILE_SIZE_PX}px`,
              height: `${ICON_TILE_SIZE_PX}px`,
              backgroundColor: color.primary,
              borderRadius: "4px",
              color: color.white,
            }}
          >
            <ListAltOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.xs}px`, minWidth: 0 }}>
            <Typography sx={{ fontSize: "12px", lineHeight: 1.25 }}>
              {translate("salesforceDashboard.records.overline")}
            </Typography>
            <Typography component="h1" sx={{ m: 0, fontSize: "18px", fontWeight: 700, lineHeight: 1.25 }}>
              {drillDown.title}
            </Typography>
            {!loading && !failed && (
              <Typography sx={{ fontSize: "12px" }}>
                {totalCount > SHOWN_RECORD_LIMIT
                  ? translate("salesforceDashboard.records.truncated", {
                      shown: formatCount(SHOWN_RECORD_LIMIT),
                      total: formatCount(totalCount),
                    })
                  : translate("salesforceDashboard.records.count", { total: formatCount(totalCount) })}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          component={RouterLink}
          startIcon={<ArrowBackOutlinedIcon />}
          sx={{
            height: "32px",
            backgroundColor: colors.cardBackground,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: "4px",
            fontSize: "13px",
            textTransform: "none",
            color: colors.link,
            "&:hover": { backgroundColor: colors.headerBackground, border: `1px solid ${colors.cardBorder}` },
          }}
          to={SALESFORCE_DASHBOARD_ROUTE}
          variant="outlined"
        >
          {translate("salesforceDashboard.records.back")}
        </Button>
      </Box>
      {loading && <CircularProgress aria-label={translate("salesforceDashboard.loading")} />}
      {failed && <Alert severity="error">{translate("salesforceDashboard.records.loadError")}</Alert>}
      {!loading &&
        !failed &&
        (totalCount === 0 ? <Typography>{translate("salesforceDashboard.records.empty")}</Typography> : table)}
    </Box>
  );
};

const RecordsRoute = () => {
  const translate = useTranslate();
  const { search } = useLocation();
  const drillDown = useMemo(() => parseDrillDown(search), [search]);

  if (!drillDown) {
    return (
      <Box sx={{ p: `${space.xl}px` }}>
        <Alert severity="warning">{translate("salesforceDashboard.records.invalid")}</Alert>
      </Box>
    );
  }
  return <RecordsContent drillDown={drillDown} />;
};

export const SalesforceRecordsPage = () => (
  <RequirePermission permission={ResourceName.SalesforceDashboard}>
    <RecordsRoute />
  </RequirePermission>
);
